// src/components/AnnotationCanvas.tsx
/**
 * Annotation Canvas Component (T-18)
 * 
 * Main canvas overlay for creating, rendering, and editing annotations.
 * Handles all annotation modes: text, equations, pen, highlighter, shapes, etc.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../types/redux';
import { throttle } from 'lodash';
import { 
  addShape, 
  addText, 
  addStroke, 
  updateShape, 
  updateText, 
  updateStroke,
  setAnnotationSelected,
  deleteAnnotation,
  deselectAllAnnotations,
  convertAnnotationCoordinates
} from '../store/slices/annotationsSlice';
import { 
  setSelectedObjects, 
  clearSelection,
  addToSelection,
  removeFromSelection,
  setActiveTool 
} from '../store/slices/uiSlice';
import MathRenderer from './MathRenderer';
import type { AnnotationShape, AnnotationText, AnnotationStroke } from '../types/redux';

// Enhanced stroke smoothing for the Number Line Web App

interface Point {
  x: number;
  y: number;
  timestamp?: number;
}

/**
 * Apply smoothing to drawing points using weighted averaging
 * This significantly reduces jagged lines during drawing
 */
export function smoothStrokePoints(points: Point[], smoothingFactor: number = 0.3): Point[] {
  if (points.length < 3) return points;
  
  const smoothedPoints: Point[] = [points[0]]; // Keep first point unchanged
  
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const current = points[i];
    const next = points[i + 1];
    
    // Weighted average of surrounding points
    const smoothedX = prev.x * smoothingFactor + 
                     current.x * (1 - 2 * smoothingFactor) + 
                     next.x * smoothingFactor;
    const smoothedY = prev.y * smoothingFactor + 
                     current.y * (1 - 2 * smoothingFactor) + 
                     next.y * smoothingFactor;
    
    smoothedPoints.push({ x: smoothedX, y: smoothedY });
  }
  
  smoothedPoints.push(points[points.length - 1]); // Keep last point unchanged
  return smoothedPoints;
}

/**
 * Reduce point density while maintaining stroke quality
 * Removes redundant points that are too close together
 */
export function optimizeStrokePoints(points: Point[], minDistance: number = 2): Point[] {
  if (points.length < 2) return points;
  
  const optimized: Point[] = [points[0]];
  
  for (let i = 1; i < points.length; i++) {
    const lastPoint = optimized[optimized.length - 1];
    const currentPoint = points[i];
    
    const distance = Math.sqrt(
      Math.pow(currentPoint.x - lastPoint.x, 2) + 
      Math.pow(currentPoint.y - lastPoint.y, 2)
    );
    
    // Only add point if it's far enough from the last one
    if (distance >= minDistance) {
      optimized.push(currentPoint);
    }
  }
  
  // Always include the last point
  if (optimized[optimized.length - 1] !== points[points.length - 1]) {
    optimized.push(points[points.length - 1]);
  }
  
  return optimized;
}

/**
 * Generate smooth Bézier curve path from points
 * Creates much smoother lines than linear connections
 */
export function generateSmoothPath(points: Point[]): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  
  // Apply smoothing and optimization
  const smoothed = smoothStrokePoints(points, 0.2);
  const optimized = optimizeStrokePoints(smoothed, 1.5);
  
  let path = `M ${optimized[0].x} ${optimized[0].y}`;
  
  for (let i = 1; i < optimized.length - 2; i++) {
    const current = optimized[i];
    const next = optimized[i + 1];
    
    // Calculate control points for smooth curves
    const controlPoint1X = current.x + (next.x - optimized[i - 1].x) * 0.2;
    const controlPoint1Y = current.y + (next.y - optimized[i - 1].y) * 0.2;
    const controlPoint2X = next.x - (optimized[i + 2].x - current.x) * 0.2;
    const controlPoint2Y = next.y - (optimized[i + 2].y - current.y) * 0.2;
    
    path += ` C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${next.x} ${next.y}`;
  }
  
  // Handle the last segment
  if (optimized.length > 2) {
    const secondLast = optimized[optimized.length - 2];
    const last = optimized[optimized.length - 1];
    path += ` L ${last.x} ${last.y}`;
  }
  
  return path;
}

/**
 * Enhanced pointer event handling with velocity-based smoothing
 */
export class SmoothDrawingHandler {
  private points: Point[] = [];
  private lastTimestamp: number = 0;
  private velocityBuffer: number[] = [];
  
  addPoint(x: number, y: number): Point[] {
    const timestamp = performance.now();
    const deltaTime = timestamp - this.lastTimestamp;
    
    // Calculate velocity for adaptive smoothing
    if (this.points.length > 0) {
      const lastPoint = this.points[this.points.length - 1];
      const distance = Math.sqrt(
        Math.pow(x - lastPoint.x, 2) + Math.pow(y - lastPoint.y, 2)
      );
      const velocity = deltaTime > 0 ? distance / deltaTime : 0;
      
      this.velocityBuffer.push(velocity);
      if (this.velocityBuffer.length > 5) {
        this.velocityBuffer.shift();
      }
      
      // Skip points if drawing too fast (reduces jitter)
      const avgVelocity = this.velocityBuffer.reduce((a, b) => a + b) / this.velocityBuffer.length;
      if (avgVelocity > 2 && distance < 3) {
        return this.points; // Skip this point
      }
    }
    
    this.points.push({ x, y, timestamp });
    this.lastTimestamp = timestamp;
    
    return this.points;
  }
  
  reset(): void {
    this.points = [];
    this.velocityBuffer = [];
    this.lastTimestamp = 0;
  }
  
  getSmoothedPoints(): Point[] {
    return this.points.length > 2 ? smoothStrokePoints(this.points, 0.25) : this.points;
  }
}

interface AnnotationCanvasProps {
  /** Canvas dimensions */
  width: number;
  height: number;
  /** Whether annotations are enabled */
  enabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Z-index for layering */
  zIndex?: number;
  /** Whether to pan with number line */
  panWithNumberLine?: boolean;
  /** Viewport offset for panning */
  viewportOffset?: { x: number; y: number };
  /** Viewport scale for zooming */
  viewportScale?: number;
  /** Number line orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Number line boundaries for clipping */
  lineBounds?: { start: number; end: number };
  /** Container scroll offset for tracking scroll position (e.g., from EquivalentFractionsView) */
  containerScrollOffset?: { x: number; y: number };
}

interface DrawingState {
  isDrawing: boolean;
  startPoint: { x: number; y: number } | null;
  currentPoints: Array<{ x: number; y: number }>;
  currentTimestamps: Array<number>;
  currentAnnotation: string | null;
  lastPoint: { x: number; y: number } | null;
  lastTime: number | null;
}

interface DragState {
  isDragging: boolean;
  draggedAnnotation: string | null;
  startPoint: { x: number; y: number } | null;
  offset: { x: number; y: number } | null;
}

/**
 * Create arrowhead path for line annotations
 */
function createLineArrowheadPath(
  x: number, 
  y: number, 
  direction: 'start' | 'end', 
  lineThickness: number = 2,
  lineStartX?: number,
  lineStartY?: number,
  lineEndX?: number,
  lineEndY?: number
): string {
  // Make arrow significantly bigger than line width for better visibility
  const arrowLength = Math.max(8, lineThickness * 3);
  
  // Calculate line angle if line coordinates are provided
  let angle = 0;
  if (lineStartX !== undefined && lineStartY !== undefined && lineEndX !== undefined && lineEndY !== undefined) {
    // Calculate angle from line direction
    const deltaX = lineEndX - lineStartX;
    const deltaY = lineEndY - lineStartY;
    angle = Math.atan2(deltaY, deltaX);
    
    // For start arrow, point away from line (opposite direction)
    // For end arrow, point away from line (continue in same direction)
    if (direction === 'start') {
      angle = angle + Math.PI; // Reverse direction for start arrow
    }
  } else {
    // Fallback for old behavior (horizontal arrows only)
    if (direction === 'start') {
      angle = Math.PI; // Point left
    } else {
      angle = 0; // Point right
    }
  }
  
  // Calculate arrow tip positions
  const tipX1 = x + Math.cos(angle + Math.PI * 0.8) * arrowLength;
  const tipY1 = y + Math.sin(angle + Math.PI * 0.8) * arrowLength;
  const tipX2 = x + Math.cos(angle - Math.PI * 0.8) * arrowLength;
  const tipY2 = y + Math.sin(angle - Math.PI * 0.8) * arrowLength;
  
  return `M ${x} ${y} L ${tipX1} ${tipY1} M ${x} ${y} L ${tipX2} ${tipY2}`;
}

export default function AnnotationCanvas({
  width,
  height,
  enabled = true,
  className = '',
  zIndex = 10,
  panWithNumberLine = false,
  viewportOffset = { x: 0, y: 0 },
  viewportScale = 1,
  orientation = 'horizontal',
  lineBounds,
  containerScrollOffset,
}: AnnotationCanvasProps) {
  const dispatch = useDispatch();
  const ui = useSelector((state: RootState) => state.ui);
  const annotations = useSelector((state: RootState) => state.annotations);
  const activeToolMode = useSelector((state: RootState) => state.toolMode?.activeTool || 'pan');
  const preferences = useSelector((state: RootState) => state.preferences);
  const isDark = preferences.theme === 'dark';
  const textScale = preferences.textScale ?? 1;
  
  // Track previous panning mode for coordinate conversion
  const previousPanModeRef = useRef(panWithNumberLine);
  const hasInitializedRef = useRef(false);
  const viewportOffsetRef = useRef(viewportOffset);
  
  // Ref to track annotations for selection checking
  const annotationsRef = useRef(annotations);
  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);
  
  const canvasRef = useRef<HTMLDivElement>(null);
  
  // Add this ref after your existing refs
  const smoothDrawingHandlerRef = useRef<SmoothDrawingHandler>(new SmoothDrawingHandler());
  
  const [drawingState, setDrawingState] = useState<DrawingState>({
    isDrawing: false,
    startPoint: null,
    currentPoints: [],
    currentTimestamps: [],
    currentAnnotation: null,
    lastPoint: null,
    lastTime: null,
  });

  // Drag state for moving annotations
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedAnnotation: null,
    startPoint: null,
    offset: null,
  });

  // Text editing state
  const [editingText, setEditingText] = useState<{
    id: string | null;
    position: { x: number; y: number };
    content: string;
    isEquation: boolean;
  }>({
    id: null,
    position: { x: 0, y: 0 },
    content: '',
    isEquation: false,
  });

  // Transform world coordinates to screen coordinates
  const worldToScreen = useCallback((x: number, y: number) => {
    const isVertical = orientation === 'vertical';
    if (isVertical) {
      // Map world Y to screen Y in vertical mode: px = worldY * scale + offsetY
      const screenY = y * viewportScale + viewportOffset.y;
      return {
        x,
        y: panWithNumberLine ? screenY : y,
      };
    }

    // Horizontal mode: world X is the value axis
    const screenX = x * viewportScale + viewportOffset.x;
    // Apply vertical scroll offset when panWithNumberLine is enabled
    const screenY = panWithNumberLine && containerScrollOffset
      ? y + containerScrollOffset.y
      : y;
    return {
      x: panWithNumberLine ? screenX : x,
      y: screenY,
    };
  }, [orientation, panWithNumberLine, viewportOffset.x, viewportOffset.y, viewportScale, containerScrollOffset]);

  // Transform screen coordinates to world coordinates
  const screenToWorld = useCallback((x: number, y: number) => {
    const isVertical = orientation === 'vertical';
    if (isVertical) {
      const worldY = (y - viewportOffset.y) / viewportScale;
      return {
        x,
        y: panWithNumberLine ? worldY : y,
      };
    }

    const worldX = (x - viewportOffset.x) / viewportScale;
    // Subtract scroll offset when converting back to world coordinates
    const worldY = panWithNumberLine && containerScrollOffset
      ? y - containerScrollOffset.y
      : y;
    return {
      x: panWithNumberLine ? worldX : x,
      y: worldY,
    };
  }, [orientation, panWithNumberLine, viewportOffset.x, viewportOffset.y, viewportScale, containerScrollOffset]);

  // Get all annotations sorted by z-index with transformed coordinates
  const allAnnotations = React.useMemo(() => {
    const all = [
      ...annotations.shapes.map(s => ({ 
        ...s, 
        displayGeometry: s.type === 'line' ? {
          x: worldToScreen(s.geometry.x, s.geometry.y).x,
          y: worldToScreen(s.geometry.x, s.geometry.y).y,
          x2: worldToScreen(s.geometry.x2 || s.geometry.x, s.geometry.y2 || s.geometry.y).x,
          y2: worldToScreen(s.geometry.x2 || s.geometry.x, s.geometry.y2 || s.geometry.y).y,
        } : s.type === 'circle' ? {
          x: worldToScreen(s.geometry.x, s.geometry.y).x,
          y: worldToScreen(s.geometry.x, s.geometry.y).y,
          radius: s.geometry.radius, // Always in screen pixels, no scaling needed
        } : {
          x: worldToScreen(s.geometry.x, s.geometry.y).x,
          y: worldToScreen(s.geometry.x, s.geometry.y).y,
          width: s.geometry.width,   // Always in screen pixels, no scaling needed
          height: s.geometry.height, // Always in screen pixels, no scaling needed
        },
      })),
      ...annotations.texts.map(t => ({ 
        ...t, 
        displayPosition: worldToScreen(t.position.x, t.position.y),
      })),
      ...annotations.strokes.map(s => ({ 
        ...s, 
        displayPoints: s.points.map(p => worldToScreen(p.x, p.y)),
      })),
    ];
    return all.sort((a, b) => a.zIndex - b.zIndex);
  }, [annotations, worldToScreen]);

  // Prevent text input from being cleared when switching tools
  useEffect(() => {
    // Only clear text input if we're switching to a non-text tool AND we're not currently editing
    if (!['text', 'equation'].includes(ui.activeTool) && editingText.content === '' && editingText.id === null) {
      setEditingText({
        id: null,
        position: { x: 0, y: 0 },
        content: '',
        isEquation: false,
      });
    }
  }, [ui.activeTool, editingText.content, editingText.id]);

  // Clear any stale drawing state on mount
  useEffect(() => {
    setDrawingState({
      isDrawing: false,
      startPoint: null,
      currentPoints: [],
      currentTimestamps: [],
      currentAnnotation: null,
      lastPoint: null,
      lastTime: null,
    });
    setDragState({
      isDragging: false,
      draggedAnnotation: null,
      startPoint: null,
      offset: null,
    });
  }, []);

  // Update viewport offset ref when it changes
  useEffect(() => {
    viewportOffsetRef.current = viewportOffset;
  }, [viewportOffset]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        const selectedAnnotations = allAnnotations.filter(a => a.selected);
        if (selectedAnnotations.length > 0) {
          selectedAnnotations.forEach(annotation => {
            dispatch(deleteAnnotation(annotation.id));
          });
          dispatch(clearSelection());
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [allAnnotations, dispatch]);

  // Find annotation at point (simple hit testing)
  const findAnnotationAtPoint = useCallback((x: number, y: number) => {
    // Check in reverse z-index order (top to bottom)
    for (let i = allAnnotations.length - 1; i >= 0; i--) {
      const annotation = allAnnotations[i];
      
      if (annotation.type === 'text' || annotation.type === 'equation') {
        // Simple bounding box check for text using display coordinates
        const text = annotation as AnnotationText & { displayPosition: { x: number; y: number } };
        const bounds = {
          left: text.displayPosition.x - 50,
          right: text.displayPosition.x + 100,
          top: text.displayPosition.y - 20,
          bottom: text.displayPosition.y + 20,
        };
        if (x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom) {
          return annotation;
        }
      } else if (annotation.type === 'rectangle' || annotation.type === 'rectangle-filled') {
        // Simple bounding box check for rectangles using display coordinates
        const shape = annotation as AnnotationShape & { displayGeometry: { x: number; y: number; width?: number; height?: number } };
        const bounds = {
          left: shape.displayGeometry.x,
          right: shape.displayGeometry.x + (shape.displayGeometry.width || 0),
          top: shape.displayGeometry.y,
          bottom: shape.displayGeometry.y + (shape.displayGeometry.height || 0),
        };
        if (x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom) {
          return annotation;
        }
      } else if (annotation.type === 'line') {
        // Line hit testing using distance from line segment
        const shape = annotation as AnnotationShape & { displayGeometry: { x: number; y: number; x2?: number; y2?: number } };
        const x1 = shape.displayGeometry.x;
        const y1 = shape.displayGeometry.y;
        const x2 = shape.displayGeometry.x2 || shape.displayGeometry.x;
        const y2 = shape.displayGeometry.y2 || shape.displayGeometry.y;
        const threshold = shape.style.thickness + 10;
        if (distanceToLine(x, y, x1, y1, x2, y2) < threshold) {
          return annotation;
        }
      } else if (annotation.type === 'circle') {
        // Circle hit testing using distance from center
        const shape = annotation as AnnotationShape & { displayGeometry: { x: number; y: number; radius?: number } };
        const distance = Math.sqrt(Math.pow(x - shape.displayGeometry.x, 2) + Math.pow(y - shape.displayGeometry.y, 2));
        const threshold = shape.style.thickness + 5;
        if (Math.abs(distance - (shape.displayGeometry.radius || 0)) <= threshold) {
          return annotation;
        }
      } else if (annotation.type === 'pen' || annotation.type === 'highlighter') {
        // Check if point is near any stroke segment using display coordinates
        const stroke = annotation as AnnotationStroke & { displayPoints: Array<{ x: number; y: number }> };
        const threshold = Math.max(stroke.style.thickness + 8, 12); // Minimum threshold of 12px
        
        // Check each segment of the stroke
        for (let j = 0; j < stroke.displayPoints.length - 1; j++) {
          const p1 = stroke.displayPoints[j];
          const p2 = stroke.displayPoints[j + 1];
          if (distanceToLine(x, y, p1.x, p1.y, p2.x, p2.y) < threshold) {
            return annotation;
          }
        }
        
        // Also check if point is near any individual point (for single-point strokes)
        if (stroke.displayPoints.length === 1) {
          const point = stroke.displayPoints[0];
          const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
          if (distance < threshold) {
            return annotation;
          }
        }
      }
    }
    return null;
  }, [allAnnotations]);

  // Handle coordinate system conversion when panning mode changes
  useEffect(() => {
    // This effect runs when panWithNumberLine changes
    // We need to convert existing annotations to maintain their visual position
    
    // Skip the first render to avoid converting on initial mount
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousPanModeRef.current = panWithNumberLine;
      return;
    }
    
    // Only convert if the mode actually changed
    if (previousPanModeRef.current !== panWithNumberLine) {
      dispatch(convertAnnotationCoordinates({
        fromPanningMode: previousPanModeRef.current,
        toPanningMode: panWithNumberLine,
        viewportOffset: viewportOffsetRef.current,
        viewportScale: viewportScale,
        orientation,
      }));
      
      // Update the previous mode
      previousPanModeRef.current = panWithNumberLine;
    }
    
    // Clear drawing state to prevent issues during mode switch
    setDrawingState({
      isDrawing: false,
      startPoint: null,
      currentPoints: [],
      currentTimestamps: [],
      currentAnnotation: null,
      lastPoint: null,
      lastTime: null,
    });
  }, [panWithNumberLine, dispatch]);

// Handle mouse/touch start
const handlePointerStart = useCallback((event: React.PointerEvent) => {
  if (!enabled) return;
  
  // Only handle events when in annotation mode
  if (activeToolMode !== 'annotation') return;
  
  // Don't handle pointer events if we're editing text
  if (editingText.content !== '' || editingText.id !== null) return;
  
  const rect = canvasRef.current?.getBoundingClientRect();
  if (!rect) return;

  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  // For select tool: check if we're clicking on an annotation first
  if (ui.activeTool === 'select') {
    const hitAnnotation = findAnnotationAtPoint(x, y);
    if (hitAnnotation) {
      // Clicking on annotation - handle selection and prevent event bubbling
      event.preventDefault();
      event.stopPropagation();
      
      const ctrlKey = event.ctrlKey || event.metaKey;
      
      // Handle selection
      if (!hitAnnotation.selected) {
        if (ctrlKey) {
          // Add to selection
          dispatch(setAnnotationSelected({ id: hitAnnotation.id, selected: true }));
          dispatch(addToSelection(hitAnnotation.id));
        } else {
          // Replace selection
          dispatch(deselectAllAnnotations());
          dispatch(setSelectedObjects([hitAnnotation.id]));
          dispatch(setAnnotationSelected({ id: hitAnnotation.id, selected: true }));
        }
      } else if (ctrlKey) {
        // Remove from selection
        dispatch(setAnnotationSelected({ id: hitAnnotation.id, selected: false }));
        dispatch(removeFromSelection(hitAnnotation.id));
      }
      
      // Start dragging
      setDragState({
        isDragging: true,
        draggedAnnotation: hitAnnotation.id,
        startPoint: { x, y },
        offset: { x: 0, y: 0 },
      });
      return;
    } else {
      // FIXED: Only dispatch ONE action to prevent infinite loop
      // Check if we actually have selected annotations before clearing
      const hasSelected = annotationsRef.current.shapes.some(s => s.selected) || 
                         annotationsRef.current.texts.some(t => t.selected) || 
                         annotationsRef.current.strokes.some(s => s.selected);
      
      if (hasSelected && !event.ctrlKey && !event.metaKey) {
        // Only clear annotations, don't touch UI selection to prevent loop
        dispatch(deselectAllAnnotations());
      }
      // Don't prevent default - let the event reach NumberLine for panning
      return;
    }
  }

  // For all annotation tools - handle normally and prevent default
  event.preventDefault();
  
  // Capture pointer to ensure all events come to this element during drawing (important for tablets)
  if (event.target instanceof Element) {
    (event.target as Element).setPointerCapture(event.pointerId);
  }
  
  switch (ui.activeTool) {
    case 'text':
    case 'equation':
      handleTextStart(x, y, ui.activeTool === 'equation');
      break;
    case 'pen':
    case 'highlighter':
      handleStrokeStart(x, y);
      break;
    case 'line':
    case 'rectangle':
    case 'rectangle-filled':
    case 'circle':
      handleShapeStart(x, y);
      break;
    case 'eraser':
      handleEraserStart(x, y);
      break;
  }
}, [enabled, activeToolMode, ui.activeTool, editingText.content, editingText.id, findAnnotationAtPoint, dispatch]);
    
  // Handle mouse/touch move
  const handlePointerMove = useCallback((event: React.PointerEvent) => {
    // Only handle events when in annotation mode
    if (activeToolMode !== 'annotation') return;
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Handle dragging annotations
    if (dragState.isDragging && dragState.draggedAnnotation && dragState.startPoint) {
      const deltaX = x - dragState.startPoint.x;
      const deltaY = y - dragState.startPoint.y;
      
      // Convert screen delta to world delta
      const worldDelta = screenToWorld(deltaX, deltaY);
      const worldDeltaX = worldDelta.x - screenToWorld(0, 0).x;
      const worldDeltaY = worldDelta.y - screenToWorld(0, 0).y;
      
      // Update annotation position
      const annotation = annotations.shapes.find(a => a.id === dragState.draggedAnnotation) ||
                        annotations.texts.find(a => a.id === dragState.draggedAnnotation) ||
                        annotations.strokes.find(a => a.id === dragState.draggedAnnotation);
      if (annotation) {
        if (annotation.type === 'text' || annotation.type === 'equation') {
          const text = annotation as AnnotationText;
          dispatch(updateText({
            id: text.id,
            updates: {
              position: {
                x: text.position.x + worldDeltaX,
                y: text.position.y + worldDeltaY,
              }
            }
          }));
        } else if (annotation.type === 'rectangle' || annotation.type === 'rectangle-filled' || annotation.type === 'circle') {
          const shape = annotation as AnnotationShape;
          dispatch(updateShape({
            id: shape.id,
            updates: {
              geometry: {
                ...shape.geometry,
                x: shape.geometry.x + worldDeltaX,
                y: shape.geometry.y + worldDeltaY,
              }
            }
          }));
        } else if (annotation.type === 'line') {
          const shape = annotation as AnnotationShape;
          dispatch(updateShape({
            id: shape.id,
            updates: {
              geometry: {
                ...shape.geometry,
                x: shape.geometry.x + worldDeltaX,
                y: shape.geometry.y + worldDeltaY,
                x2: (shape.geometry.x2 || shape.geometry.x) + worldDeltaX,
                y2: (shape.geometry.y2 || shape.geometry.y) + worldDeltaY,
              }
            }
          }));
        } else if (annotation.type === 'pen' || annotation.type === 'highlighter') {
          const stroke = annotation as AnnotationStroke;
          const newPoints = stroke.points.map(point => ({
            x: point.x + worldDeltaX,
            y: point.y + worldDeltaY,
          }));
          dispatch(updateStroke({
            id: stroke.id,
            updates: {
              points: newPoints,
            }
          }));
        }
      }
      
      // Update drag state
      setDragState(prev => ({
        ...prev,
        startPoint: { x, y },
      }));
      return;
    }

    // Handle drawing
    if (!drawingState.isDrawing) return;

    if (ui.activeTool === 'pen' || ui.activeTool === 'highlighter') {
      // Use the smooth drawing handler
      const smoothedPoints = smoothDrawingHandlerRef.current.addPoint(x, y);
      
      setDrawingState(prev => ({
        ...prev,
        currentPoints: smoothedPoints,
        currentTimestamps: [...prev.currentTimestamps, Date.now()],
        lastPoint: { x, y },
        lastTime: Date.now(),
      }));
    } else if (['line', 'rectangle', 'rectangle-filled', 'circle'].includes(ui.activeTool)) {
      // Update preview for shape tools
      setDrawingState(prev => ({ ...prev, currentPoints: [{ x, y }] }));
    }
  }, [dragState, drawingState.isDrawing, ui.activeTool, activeToolMode, dispatch, annotations, screenToWorld]);

  // Handle mouse/touch end
  const handlePointerEnd = useCallback((event?: React.PointerEvent) => {
    // Only handle events when in annotation mode
    if (activeToolMode !== 'annotation') return;
    
    // Release pointer capture if we have an event (important for tablets)
    if (event && canvasRef.current) {
      try {
        canvasRef.current.releasePointerCapture(event.pointerId);
      } catch {
        // Pointer may already be released
      }
    }
    
    // Stop dragging
    if (dragState.isDragging) {
      setDragState({
        isDragging: false,
        draggedAnnotation: null,
        startPoint: null,
        offset: null,
      });
      return;
    }

    // Handle drawing
    if (!drawingState.isDrawing) return;

    switch (ui.activeTool) {
      case 'pen':
      case 'highlighter':
        handleStrokeEnd();
        break;
      case 'line':
      case 'rectangle':
      case 'rectangle-filled':
      case 'circle':
        handleShapeEnd();
        break;
    }

    setDrawingState({
      isDrawing: false,
      startPoint: null,
      currentPoints: [],
      currentTimestamps: [],
      currentAnnotation: null,
      lastPoint: null,
      lastTime: null,
    });
  }, [dragState.isDragging, drawingState, ui.activeTool, activeToolMode]);

  // Add this throttled version for high-frequency events
  const throttledPointerMove = useCallback(
    throttle(handlePointerMove, 16), // ~60fps
    [handlePointerMove]
  );

  // Text creation handling
  const handleTextStart = (x: number, y: number, isEquation: boolean) => {
    setEditingText({
      id: null,
      position: { x, y },
      content: '',
      isEquation,
    });
  };

  // Stroke drawing handling
  const handleStrokeStart = (x: number, y: number) => {
    // Reset the smooth drawing handler
    smoothDrawingHandlerRef.current.reset();
    
    // Add the first point
    const initialPoints = smoothDrawingHandlerRef.current.addPoint(x, y);
    
    setDrawingState({
      isDrawing: true,
      startPoint: { x, y },
      currentPoints: initialPoints,
      currentTimestamps: [Date.now()],
      currentAnnotation: null,
      lastPoint: { x, y },
      lastTime: Date.now(),
    });
  };

  // Smooth stroke points using a simple moving average
  // REMOVED: Replaced with enhanced SmoothDrawingHandler
  /*
  const smoothPoints = (points: Array<{ x: number; y: number }>, smoothingFactor: number = 0.3) => {
    if (points.length < 3) return points;
    
    const smoothed = [points[0]]; // Keep first point
    
    for (let i = 1; i < points.length - 1; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];
      
      // Apply smoothing: weighted average of current point with neighbors
      const smoothedX = curr.x * (1 - smoothingFactor) + (prev.x + next.x) * smoothingFactor * 0.5;
      const smoothedY = curr.y * (1 - smoothingFactor) + (prev.y + next.y) * smoothingFactor * 0.5;
      
      smoothed.push({ x: smoothedX, y: smoothedY });
    }
    
    smoothed.push(points[points.length - 1]); // Keep last point
    return smoothed;
  };
  */

  // Enhanced stroke smoothing with velocity-based adaptive smoothing
  // REMOVED: Replaced with enhanced SmoothDrawingHandler
  /*
  const smoothPointsEnhanced = (points: Array<{ x: number; y: number }>, timestamps: Array<number>) => {
    if (points.length < 3) return points;
    
    const smoothed = [points[0]]; // Keep first point
    
    for (let i = 1; i < points.length - 1; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];
      
      // Calculate velocity (distance per time)
      const timeToPrev = timestamps[i] - timestamps[i - 1];
      const timeToNext = timestamps[i + 1] - timestamps[i];
      const distToPrev = Math.sqrt(Math.pow(curr.x - prev.x, 2) + Math.pow(curr.y - prev.y, 2));
      const distToNext = Math.sqrt(Math.pow(next.x - curr.x, 2) + Math.pow(next.y - curr.y, 2));
      
      const velocityPrev = timeToPrev > 0 ? distToPrev / timeToPrev : 0;
      const velocityNext = timeToNext > 0 ? distToNext / timeToNext : 0;
      const avgVelocity = (velocityPrev + velocityNext) / 2;
      
      // Adaptive smoothing factor based on velocity
      // Higher velocity = more smoothing needed
      const baseSmoothingFactor = 0.4;
      const velocityMultiplier = Math.min(avgVelocity / 2, 1.5); // Cap at 1.5x
      const adaptiveSmoothingFactor = Math.min(baseSmoothingFactor * velocityMultiplier, 0.8);
      
      // Apply smoothing with adaptive factor
      const smoothedX = curr.x * (1 - adaptiveSmoothingFactor) + (prev.x + next.x) * adaptiveSmoothingFactor * 0.5;
      const smoothedY = curr.y * (1 - adaptiveSmoothingFactor) + (prev.y + next.y) * adaptiveSmoothingFactor * 0.5;
      
      smoothed.push({ x: smoothedX, y: smoothedY });
    }
    
    smoothed.push(points[points.length - 1]); // Keep last point
    return smoothed;
  };
  */

  // Enhanced point interpolation for smoother curves
  // REMOVED: Replaced with enhanced SmoothDrawingHandler
  /*
  const interpolatePoints = (startPoint: { x: number; y: number }, endPoint: { x: number; y: number }, 
                           startTime: number, endTime: number, minDistance: number = 2) => {
    const distance = Math.sqrt(Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2));
    const timeDelta = endTime - startTime;
    
    if (distance < minDistance || timeDelta < 5) {
      return [endPoint]; // No interpolation needed
    }
    
    // Calculate optimal number of points based on distance and speed
    const speed = distance / timeDelta;
    const basePoints = Math.max(2, Math.min(Math.floor(distance / 3), 8)); // Between 2 and 8 points
    const speedMultiplier = Math.min(speed / 1.5, 2); // Cap at 2x for very fast strokes
    const numPoints = Math.floor(basePoints * speedMultiplier);
    
    const points = [];
    for (let i = 1; i <= numPoints; i++) {
      const t = i / (numPoints + 1);
      // Use ease-out curve for more natural interpolation
      const easeT = 1 - Math.pow(1 - t, 2);
      const interpolatedX = startPoint.x + (endPoint.x - startPoint.x) * easeT;
      const interpolatedY = startPoint.y + (endPoint.y - startPoint.y) * easeT;
      points.push({ x: interpolatedX, y: interpolatedY });
    }
    
    return points;
  };
  */

  const handleStrokeEnd = () => {
    if (drawingState.currentPoints.length < 2) return;

    const strokeType = ui.activeTool === 'highlighter' ? 'highlighter' : 'pen';
    
    // Get the final smoothed points from the handler
    const finalSmoothedPoints = smoothDrawingHandlerRef.current.getSmoothedPoints();
    
    // Apply final optimization
    const optimizedPoints = optimizeStrokePoints(finalSmoothedPoints, 1.5);
    
    // Convert screen coordinates to world coordinates
    const worldPoints = optimizedPoints.map(p => screenToWorld(p.x, p.y));
    
    dispatch(addStroke({
      type: strokeType,
      points: worldPoints,
      style: {
        color: ui.colorPicker.activeColor,
        thickness: ui.thickness,
        opacity: strokeType === 'highlighter' ? 0.5 : 1.0,
      },
    }));
  };

  // Shape creation handling
  const handleShapeStart = (x: number, y: number) => {
    setDrawingState({
      isDrawing: true,
      startPoint: { x, y },
      currentPoints: [],
      currentTimestamps: [],
      currentAnnotation: null,
      lastPoint: null,
      lastTime: null,
    });
  };

  const handleShapeEnd = () => {
    if (!drawingState.startPoint || drawingState.currentPoints.length === 0) return;

    const start = drawingState.startPoint;
    const end = drawingState.currentPoints[drawingState.currentPoints.length - 1];
    
    // Convert screen coordinates to world coordinates for position
    const worldStart = screenToWorld(start.x, start.y);
    const worldEnd = screenToWorld(end.x, end.y);
    
    // Keep size calculations in screen coordinates for consistency
    const screenWidth = Math.abs(end.x - start.x);
    const screenHeight = Math.abs(end.y - start.y);
    const screenRadius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
    
    const shape: Omit<AnnotationShape, 'id' | 'zIndex' | 'selected'> = {
      type: ui.activeTool === 'line' ? 'line' : 
            ui.activeTool === 'rectangle-filled' ? 'rectangle-filled' : 
            ui.activeTool === 'circle' ? 'circle' : 'rectangle',
      geometry: ui.activeTool === 'line' ? {
        x: worldStart.x,
        y: worldStart.y,
        x2: worldEnd.x,
        y2: worldEnd.y,
      } : ui.activeTool === 'circle' ? {
        x: worldStart.x,
        y: worldStart.y,
        radius: screenRadius, // Always in screen pixels
      } : {
        x: Math.min(worldStart.x, worldEnd.x),
        y: Math.min(worldStart.y, worldEnd.y),
        width: screenWidth,   // Always in screen pixels
        height: screenHeight, // Always in screen pixels
      },
      style: {
        color: ui.colorPicker.activeColor,
        thickness: ui.thickness,
        arrowStart: ui.activeTool === 'line' ? ui.toolSettings.line?.arrowStart || false : false,
        arrowEnd: ui.activeTool === 'line' ? ui.toolSettings.line?.arrowEnd || false : false,
      },
    };


    dispatch(addShape(shape));
  };

  // Eraser handling
  const handleEraserStart = (x: number, y: number) => {
    const hitAnnotation = findAnnotationAtPoint(x, y);
    if (hitAnnotation) {
      dispatch(deleteAnnotation(hitAnnotation.id));
    }
  };



  // Handle text input submission
  const handleTextSubmit = () => {
    if (editingText.content.trim()) {
      // Convert screen coordinates to world coordinates
      const worldPosition = screenToWorld(editingText.position.x, editingText.position.y);
      
      dispatch(addText({
        type: editingText.isEquation ? 'equation' : 'text',
        content: editingText.content,
        position: worldPosition,
        style: {
          fontSize: 16,
          color: ui.colorPicker.activeColor,
          fontFamily: 'Arial, sans-serif',
        },
      }));
    }
    
    setEditingText({
      id: null,
      position: { x: 0, y: 0 },
      content: '',
      isEquation: false,
    });
  };

  // Handle text input cancellation
  const handleTextCancel = () => {
    setEditingText({
      id: null,
      position: { x: 0, y: 0 },
      content: '',
      isEquation: false,
    });
  };

  // Generate smooth path data using cubic Bézier curves for smoother strokes
  const generateSmoothPath = (points: Array<{ x: number; y: number }>) => {
    if (points.length < 2) return '';
    if (points.length === 2) {
      return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
    }

    let path = `M ${points[0].x} ${points[0].y}`;
    
    // Use cubic Bézier curves for smoother paths
    for (let i = 0; i < points.length - 1; i++) {
      const curr = points[i];
      const next = points[i + 1];
      
      if (i === 0) {
        // First segment: use a simple curve
        const cp1x = curr.x + (next.x - curr.x) * 0.3;
        const cp1y = curr.y + (next.y - curr.y) * 0.3;
        const cp2x = next.x - (next.x - curr.x) * 0.3;
        const cp2y = next.y - (next.y - curr.y) * 0.3;
        path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${next.x} ${next.y}`;
      } else if (i === points.length - 2) {
        // Last segment: use a simple curve
        const cp1x = curr.x + (next.x - curr.x) * 0.3;
        const cp1y = curr.y + (next.y - curr.y) * 0.3;
        const cp2x = next.x - (next.x - curr.x) * 0.3;
        const cp2y = next.y - (next.y - curr.y) * 0.3;
        path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${next.x} ${next.y}`;
      } else {
        // Middle segments: use smooth curves with better control points
        const prev = points[i - 1];
        const after = points[i + 2] || next;
        
        // Calculate smooth control points
        const dx1 = curr.x - prev.x;
        const dy1 = curr.y - prev.y;
        const dx2 = after.x - next.x;
        const dy2 = after.y - next.y;
        
        // Control point 1: extends from current point
        const cp1x = curr.x + (dx1 + dx2) * 0.25;
        const cp1y = curr.y + (dy1 + dy2) * 0.25;
        
        // Control point 2: extends toward next point
        const cp2x = next.x - (dx1 + dx2) * 0.25;
        const cp2y = next.y - (dy1 + dy2) * 0.25;
        
        path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${next.x} ${next.y}`;
      }
    }
    
    return path;
  };

  // Render stroke as SVG path
  const renderStroke = (stroke: any) => {
    if (stroke.displayPoints.length < 2) return null;

    const pathData = generateSmoothPath(stroke.displayPoints);

    return (
      <path
        key={stroke.id}
        d={pathData}
        stroke={stroke.style.color}
        strokeWidth={stroke.style.thickness}
        strokeOpacity={stroke.style.opacity}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={stroke.selected ? 'stroke-selected' : ''}
        style={{ cursor: ui.activeTool === 'select' ? 'pointer' : 'default' }}
      />
    );
  };

  // Render shape as SVG element
  const renderShape = (shape: any) => {
    const { displayGeometry, style } = shape;
    const isSelected = shape.selected;
    

    
    if (shape.type === 'line') {
      const x1 = displayGeometry.x;
      const y1 = displayGeometry.y;
      const x2 = displayGeometry.x2 || displayGeometry.x;
      const y2 = displayGeometry.y2 || displayGeometry.y;
      
      // Determine line direction for proper arrow orientation
      const isLeftToRight = x2 > x1;
      
      return (
        <g key={shape.id}>
          {/* Main line */}
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={style.color}
            strokeWidth={style.thickness}
            className={isSelected ? 'stroke-selected' : ''}
            style={{ cursor: ui.activeTool === 'select' ? 'pointer' : 'default' }}
          />
          
          {/* Start arrow */}
          {style.arrowStart && (
            <path
              d={createLineArrowheadPath(x1, y1, 'start', style.thickness, x1, y1, x2, y2)}
              stroke={style.color}
              strokeWidth={style.thickness}
              fill="none"
              strokeLinecap="round"
              className={isSelected ? 'stroke-selected' : ''}
            />
          )}
          
          {/* End arrow */}
          {style.arrowEnd && (
            <path
              d={createLineArrowheadPath(x2, y2, 'end', style.thickness, x1, y1, x2, y2)}
              stroke={style.color}
              strokeWidth={style.thickness}
              fill="none"
              strokeLinecap="round"
              className={isSelected ? 'stroke-selected' : ''}
            />
          )}
        </g>
      );
    } else if (shape.type === 'circle') {
      return (
        <circle
          key={shape.id}
          cx={displayGeometry.x}
          cy={displayGeometry.y}
          r={displayGeometry.radius}
          fill="none"
          stroke={style.color}
          strokeWidth={style.thickness}
          className={isSelected ? 'stroke-selected' : ''}
          style={{ cursor: ui.activeTool === 'select' ? 'pointer' : 'default' }}
        />
      );
    } else {
      return (
        <rect
          key={shape.id}
          x={displayGeometry.x}
          y={displayGeometry.y}
          width={displayGeometry.width}
          height={displayGeometry.height}
          fill={shape.type === 'rectangle-filled' ? style.color : 'none'}
          stroke={style.color}
          strokeWidth={style.thickness}
          className={isSelected ? 'stroke-selected' : ''}
          style={{ cursor: ui.activeTool === 'select' ? 'pointer' : 'default' }}
        />
      );
    }
  };

  // Render text annotation
  const renderText = (text: any) => {
    const scaledFontSize = text.style.fontSize * textScale;
    return (
      <foreignObject
        key={text.id}
        x={text.displayPosition.x}
        y={text.displayPosition.y - scaledFontSize}
        width={200 * textScale}
        height={scaledFontSize + 10}
        className={text.selected ? 'text-selected' : ''}
        style={{ cursor: ui.activeTool === 'select' ? 'pointer' : 'default' }}
      >
        <div
          style={{
            fontSize: scaledFontSize,
            color: text.style.color,
            fontFamily: text.style.fontFamily,
            padding: '2px 4px',
            backgroundColor: text.selected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
            border: text.selected ? '1px dashed #3B82F6' : 'none',
          }}
        >
          {text.type === 'equation' ? (
            <MathRenderer expression={text.content} />
          ) : (
            text.content
          )}
        </div>
      </foreignObject>
    );
  };

  // Render current drawing preview
  const renderPreview = () => {
    if (!drawingState.isDrawing || !drawingState.startPoint) return null;

    if (ui.activeTool === 'pen' || ui.activeTool === 'highlighter') {
      // Render current stroke
      if (drawingState.currentPoints.length < 2) return null;
      
      const pathData = generateSmoothPath(drawingState.currentPoints);

      return (
        <path
          d={pathData}
          stroke={ui.colorPicker.activeColor}
          strokeWidth={ui.thickness}
          strokeOpacity={ui.activeTool === 'highlighter' ? 0.5 : 1.0}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="stroke-preview"
        />
      );
    } else if (drawingState.currentPoints.length > 0) {
      // Render shape preview
      const start = drawingState.startPoint;
      const end = drawingState.currentPoints[drawingState.currentPoints.length - 1];
      
      if (ui.activeTool === 'line') {
        const arrowStart = ui.toolSettings.line?.arrowStart || false;
        const arrowEnd = ui.toolSettings.line?.arrowEnd || false;
        
        // Determine line direction for proper arrow orientation
        const isLeftToRight = end.x > start.x;
        
        return (
          <g className="shape-preview">
            {/* Main line */}
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={ui.colorPicker.activeColor}
              strokeWidth={ui.thickness}
              strokeDasharray="5,5"
            />
            
            {/* Start arrow */}
            {arrowStart && (
              <path
                d={createLineArrowheadPath(start.x, start.y, 'start', ui.thickness, start.x, start.y, end.x, end.y)}
                stroke={ui.colorPicker.activeColor}
                strokeWidth={ui.thickness}
                fill="none"
                strokeLinecap="round"
                strokeDasharray="5,5"
              />
            )}
            
            {/* End arrow */}
            {arrowEnd && (
              <path
                d={createLineArrowheadPath(end.x, end.y, 'end', ui.thickness, start.x, start.y, end.x, end.y)}
                stroke={ui.colorPicker.activeColor}
                strokeWidth={ui.thickness}
                fill="none"
                strokeLinecap="round"
                strokeDasharray="5,5"
              />
            )}
          </g>
        );
      } else if (ui.activeTool === 'circle') {
        const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
        return (
          <circle
            cx={start.x}
            cy={start.y}
            r={radius}
            fill="none"
            stroke={ui.colorPicker.activeColor}
            strokeWidth={ui.thickness}
            strokeDasharray="5,5"
            className="shape-preview"
          />
        );
      } else {
        return (
          <rect
            x={Math.min(start.x, end.x)}
            y={Math.min(start.y, end.y)}
            width={Math.abs(end.x - start.x)}
            height={Math.abs(end.y - start.y)}
            fill={ui.activeTool === 'rectangle' ? 'none' : ui.colorPicker.activeColor}
            stroke={ui.colorPicker.activeColor}
            strokeWidth={ui.thickness}
            strokeDasharray="5,5"
            fillOpacity={0.3}
            className="shape-preview"
          />
        );
      }
    }

    return null;
  };

  return (
    <div
      ref={canvasRef}
      className={`annotation-canvas ${className}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width,
        height,
        zIndex,
        pointerEvents: enabled && activeToolMode === 'annotation' ? 'auto' : 'none',
        cursor: enabled ? getCursorForTool(ui.activeTool) : 'default',
        overflow: panWithNumberLine ? 'hidden' : 'visible',
        // Prevent browser touch gestures (scrolling) when in annotation mode on tablets
        touchAction: enabled && activeToolMode === 'annotation' ? 'none' : 'auto',
      }}
      onPointerDown={enabled && activeToolMode === 'annotation' ? handlePointerStart : undefined}
      // USE THROTTLED VERSION FOR BETTER PERFORMANCE:
      onPointerMove={enabled && activeToolMode === 'annotation' ? throttledPointerMove : undefined}
      onPointerUp={enabled && activeToolMode === 'annotation' ? handlePointerEnd : undefined}
      onPointerLeave={enabled && activeToolMode === 'annotation' ? () => {
        if (drawingState.isDrawing) {
          handlePointerEnd();
        }
      } : undefined}
    >
      {/* SVG for all annotations */}
      <svg
        width={width}
        height={height}
        style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0,
        }}
        className="annotation-svg"
      >
        <defs>
          <clipPath id="viewport-clip">
            <rect 
              x={lineBounds?.start ?? 10} 
              y="0" 
              width={(lineBounds?.end ?? width - 10) - (lineBounds?.start ?? 10)} 
              height={height} 
            />
          </clipPath>
        </defs>
        
        <g clipPath="url(#viewport-clip)">
          {/* Render all annotations */}
          {allAnnotations.map((annotation) => {
            if (annotation.type === 'pen' || annotation.type === 'highlighter') {
              return renderStroke(annotation as AnnotationStroke);
            } else if (annotation.type === 'rectangle' || annotation.type === 'rectangle-filled' || annotation.type === 'circle' || annotation.type === 'line') {
              return renderShape(annotation as AnnotationShape);
            } else if (annotation.type === 'text' || annotation.type === 'equation') {
              return renderText(annotation as AnnotationText);
            }
            return null;
          })}
          
          {/* Render drawing preview */}
          {renderPreview()}
        </g>
      </svg>

      {/* Text editing input */}
      {(editingText.id !== null || editingText.content !== '' || editingText.position.x > 0) && (
        <div
          className="text-editor"
          style={{
            position: 'absolute',
            left: editingText.position.x,
            top: editingText.position.y - 30,
            zIndex: 1000,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
                      <div 
              className={`flex items-center gap-2 p-2 rounded shadow-lg ${
                isDark 
                  ? 'bg-gray-800 border border-gray-600' 
                  : 'bg-white border border-gray-300'
              }`}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerMove={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
            <input
              type="text"
              value={editingText.content}
              onChange={(e) => {
                e.stopPropagation();
                setEditingText(prev => ({ ...prev, content: e.target.value }));
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  handleTextSubmit();
                } else if (e.key === 'Escape') {
                  handleTextCancel();
                }
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerMove={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              placeholder={editingText.isEquation ? "Enter equation (LaTeX)" : "Enter text"}
              className={`px-2 py-1 text-sm border rounded flex-1 ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              autoFocus
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTextSubmit();
              }}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              ✓
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTextCancel();
              }}
              className={`px-2 py-1 text-xs rounded ${
                isDark 
                  ? 'bg-gray-600 text-white hover:bg-gray-500' 
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              }`}
            >
              ✕
            </button>
          </div>
          {editingText.isEquation && (
            <div className={`text-xs mt-1 px-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Preview: <MathRenderer expression={editingText.content || 'x = a + b'} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper functions
function getCursorForTool(tool: string): string {
  const cursors: Record<string, string> = {
    select: 'default',
    pan: 'grab',
    text: 'text',
    equation: 'text', 
    pen: 'crosshair',
    highlighter: 'crosshair',
    eraser: 'pointer',
    line: 'crosshair',
    rectangle: 'crosshair',
    'rectangle-filled': 'crosshair',
    circle: 'crosshair',
  };
  return cursors[tool] || 'default';
}

function distanceToLine(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  
  if (lenSq === 0) return Math.sqrt(A * A + B * B);
  
  let param = dot / lenSq;
  
  if (param < 0) {
    return Math.sqrt(A * A + B * B);
  } else if (param > 1) {
    return Math.sqrt((px - x2) * (px - x2) + (py - y2) * (py - y2));
  } else {
    const xx = x1 + param * C;
    const yy = y1 + param * D;
    return Math.sqrt((px - xx) * (px - xx) + (py - yy) * (py - yy));
  }
}